export type PatientHealthRecord = Record<string, unknown>;

export interface CursorPagination {
  next_cursor?: string | null;
  has_more?: boolean;
}

export interface NormalizedCursorPage<T extends PatientHealthRecord> {
  items: T[];
  pagination?: CursorPagination;
}

function isRecord(value: unknown): value is PatientHealthRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCursorPagination(value: unknown): value is CursorPagination {
  return (
    isRecord(value) &&
    (value.next_cursor === undefined || value.next_cursor === null || typeof value.next_cursor === 'string') &&
    (value.has_more === undefined || typeof value.has_more === 'boolean')
  );
}

export function getValue(record: PatientHealthRecord, keys: readonly string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) {
      return record[key];
    }
  }

  return undefined;
}

export function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
}

export function normalizeCursorPage<T extends PatientHealthRecord>(response: unknown): NormalizedCursorPage<T> {
  if (Array.isArray(response)) {
    return {
      items: response.filter(isRecord) as T[]
    };
  }

  if (!isRecord(response)) {
    return { items: [] };
  }

  const items = Array.isArray(response.data) ? response.data.filter(isRecord) as T[] : [];

  return {
    items,
    pagination: isCursorPagination(response.pagination) ? response.pagination : undefined
  };
}